/**
 * KAN-880: перенос карточек канбан-доски CRM (перетаскивание и "Сменить этап").
 * KAN-882: решение переноса - resolveCardDrop (lib/crmPipeline) по
 * accepts/target целевой колонки, правил видов здесь нет:
 *   - take = "Взять в работу" (useAttentionDone: менеджер берёт себе сразу,
 *     владелец/оператор/старший в кабинете - окно назначения done.dialogItem);
 *   - manager / temperature / stage = изменение клиента (useEditBoardCard):
 *     карточка сразу в целевой колонке, при ошибке откат и сообщение;
 *   - перенос запрещён - ничего.
 * Карточка из догруженной порции колонки (свёрнутая колонка, "Загрузить ещё")
 * берётся из кеша порций и оптимистично убирается из него; после ответа
 * порции перезапрашиваются вместе с доской (ATTENTION_QUERY_KEY).
 */
import type { DropResult } from '@hello-pangea/dnd';
import { useQueryClient } from '@tanstack/react-query';
import { useAttentionActions } from '@/components/dashboard/useAttentionActions';
import { useAttentionDone } from '@/components/dashboard/useAttentionDone';
import { crmColumnPagesPrefix, useEditBoardCard } from '@/hooks/useCrmPipeline';
import {
  applyPipelineAction,
  cardEditPatch,
  columnForTarget,
  findCardInPages,
  pipelineCardToAttentionItem,
  resolveCardDrop,
  withoutCardInPages,
  type CrmColumnPagesData,
} from '@/lib/crmPipeline';
import type { WorkSurface } from '@/lib/workActions';
import type { CrmBoardColumn, CrmBoardView, CrmPipelineCard, CrmPipelineResponse, CrmPipelineStage, CrmStageId } from '@/types/crmPipeline';

/** Карточки нет среди колонок (пришла порцией колонки): известен только id. */
function stubCard(id: string, from: CrmBoardColumn | undefined): CrmPipelineCard {
  return {
    id,
    name: '',
    company: null,
    avatar_url: null,
    temperature: null,
    stage_id: null,
    outcome: 'open',
    manager: null,
    last_message: null,
    last_activity_at: null,
    attention: false,
    attention_since: null,
    attention_reason: null,
    attention_task: null,
    column: from?.key,
  };
}

export function useCrmBoardMoves(
  board: CrmBoardView,
  columns: readonly CrmBoardColumn[],
  boardKey: readonly unknown[],
  surface: WorkSurface = 'cabinet'
) {
  const queryClient = useQueryClient();
  const edit = useEditBoardCard(boardKey);
  const actions = useAttentionActions<CrmPipelineResponse>(boardKey, applyPipelineAction);
  const done = useAttentionDone(actions.run, surface);

  /** Порции колонки в кеше (догруженные карточки), по любым фильтрам. */
  const pagesOf = (column: CrmBoardColumn | undefined) =>
    column ? { queryKey: crmColumnPagesPrefix(board, column.key) } : null;

  const findCard = (id: string, from: CrmBoardColumn | undefined): CrmPipelineCard => {
    const inColumn = from?.cards.find((c) => c.id === id);
    if (inColumn) return inColumn;
    const filter = pagesOf(from);
    const datasets = filter ? queryClient.getQueriesData<CrmColumnPagesData>(filter).map(([, d]) => d) : [];
    return findCardInPages(datasets, id) ?? stubCard(id, from);
  };

  const removeFromPages = (id: string, from: CrmBoardColumn | undefined) => {
    const filter = pagesOf(from);
    if (!filter) return;
    queryClient.setQueriesData<CrmColumnPagesData>(filter, (data) => (data ? withoutCardInPages(data, id) : data));
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const from = columns.find((c) => c.key === source.droppableId);
    const to = columns.find((c) => c.key === destination.droppableId);
    const card = findCard(draggableId, from);
    const drop = resolveCardDrop(card, from, to);
    if (!drop || !to) return;
    if (drop.type === 'take') {
      done.request(pipelineCardToAttentionItem(card));
      return;
    }
    removeFromPages(draggableId, from);
    edit.mutate({
      clientId: draggableId,
      edit: drop,
      patch: cardEditPatch(drop, to.title),
      toColumn: to.key,
      fromColumn: from?.key ?? null,
    });
  };

  /** "Сменить этап" (телефон): этап выбран явно, карточка в колонку этого этапа. */
  const changeStage = (card: CrmPipelineCard, stageId: CrmStageId, stages: readonly CrmPipelineStage[]) => {
    const to = columnForTarget(columns, 'stage', stageId);
    const name = to?.title ?? stages.find((s) => String(s.id) === String(stageId))?.name ?? null;
    const stageEdit = { type: 'stage', stageId } as const;
    edit.mutate({
      clientId: card.id,
      edit: stageEdit,
      patch: cardEditPatch(stageEdit, name),
      toColumn: to?.key ?? null,
      fromColumn: card.column ?? null,
    });
  };

  return { onDragEnd, changeStage, done };
}
